/* CkdStagesTable — Per-stage nutrient limits визуализация (KDIGO 2020).
 *
 * Извежда всичките 7 KDIGO стадия (G1 → G5D) като цветово-кодирана таблица
 * с protein/sodium/potassium/phosphorus/energy лимити. Поддържа BG/EN/ES.
 *
 * Optional auto-highlight на потребителския стадий, ако backend върне
 * ``current_stage`` (при подаден eGFR + weight → показва абсолютни грамове).
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Activity } from "lucide-react";

// Помощник за избор на езикова версия
const pickL = (obj, key, lang) => {
  if (!obj) return "";
  return obj[`${key}_${lang}`] ?? obj[`${key}_bg`] ?? obj[key] ?? "";
};

const UI = {
  bg: {
    title: "CKD 5 стадия — дневни хранителни лимити",
    subtitle: "KDIGO 2020 Nutrition Guidelines",
    stage: "Стадий", egfr: "eGFR диапазон", protein: "Протеин", sodium: "Натрий",
    potassium: "Калий", phosphorus: "Фосфор", energy: "Енергия", fluid: "Течности",
    flags: "Приоритет", your_stage: "Вашият стадий", absolute_title: "Персонализирани лимити (абсолютни)",
    g_per_day: "g/ден", mg_per_day: "mg/ден", kcal_per_day: "kcal/ден", weight_input: "Тегло (kg)",
    egfr_input: "eGFR (mL/min)", apply: "Приложи",
    source: "Източник:", auto_hint: "Автоматично: ако имате последен лабораторен резултат за креатинин, стадият се определя спрямо него.",
    hint_manual: "Въведете eGFR и тегло, за да видите вашите персонализирани абсолютни лимити.",
  },
  en: {
    title: "CKD 5 stages — daily nutrient limits",
    subtitle: "KDIGO 2020 Nutrition Guidelines",
    stage: "Stage", egfr: "eGFR range", protein: "Protein", sodium: "Sodium",
    potassium: "Potassium", phosphorus: "Phosphorus", energy: "Energy", fluid: "Fluid",
    flags: "Priority", your_stage: "Your stage", absolute_title: "Personalized limits (absolute)",
    g_per_day: "g/day", mg_per_day: "mg/day", kcal_per_day: "kcal/day", weight_input: "Weight (kg)",
    egfr_input: "eGFR (mL/min)", apply: "Apply",
    source: "Source:", auto_hint: "Auto: if you have a recent creatinine lab, the stage is derived from it.",
    hint_manual: "Enter eGFR and weight to see your personalized absolute limits.",
  },
  es: {
    title: "CKD 5 etapas — límites nutricionales diarios",
    subtitle: "Guía KDIGO 2020 de Nutrición",
    stage: "Etapa", egfr: "Rango de eGFR", protein: "Proteína", sodium: "Sodio",
    potassium: "Potasio", phosphorus: "Fósforo", energy: "Energía", fluid: "Líquidos",
    flags: "Prioridad", your_stage: "Su etapa", absolute_title: "Límites personalizados (absolutos)",
    g_per_day: "g/día", mg_per_day: "mg/día", kcal_per_day: "kcal/día", weight_input: "Peso (kg)",
    egfr_input: "eGFR (mL/min)", apply: "Aplicar",
    source: "Fuente:", auto_hint: "Auto: si tiene un análisis reciente de creatinina, la etapa se deriva de él.",
    hint_manual: "Ingrese eGFR y peso para ver sus límites absolutos personalizados.",
  },
};

export default function CkdStagesTable() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const lang = (language || "bg").toLowerCase();
  const t = UI[lang] || UI.bg;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [egfrInput, setEgfrInput] = useState("");
  const [weightInput, setWeightInput] = useState("");

  // Pre-fill weight от profile, ако е налично
  useEffect(() => {
    const w = user?.profile?.body?.weight;
    if (w && !weightInput) setWeightInput(String(w));
  }, [user, weightInput]);

  const fetchData = async (egfr, weight) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (egfr) params.append("egfr", egfr);
      if (weight) params.append("weight_kg", weight);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data: resp } = await axios.get(`${API}/nephrology/ckd-stage-limits${qs}`);
      setData(resp);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("CKD stages fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    const egfr = parseFloat(egfrInput);
    const weight = parseFloat(weightInput);
    fetchData(
      Number.isFinite(egfr) && egfr > 0 ? egfr : null,
      Number.isFinite(weight) && weight > 0 ? weight : null,
    );
  };

  const stages = data?.stages || [];
  const currentStage = data?.current_stage;
  const currentAbsolute = data?.current_absolute?.absolute;

  // Helper: range renderer для min-max nutrient polей
  const renderRange = (obj, unit) => {
    if (!obj) return "—";
    const min = obj.min;
    const max = obj.max;
    if (min != null && max != null && min !== max) return `${min}-${max} ${unit}`;
    if (max != null) return `<${max} ${unit}`;
    if (min != null) return `≥${min} ${unit}`;
    return "—";
  };

  return (
    <div className="space-y-3" data-testid="ckd-stages-table">
      {/* Header с input filter */}
      <div className={`rounded-lg p-3 border ${isDark ? "border-sky-500/20 bg-sky-500/5" : "border-sky-200 bg-sky-50/50"}`}>
        <div className="flex items-center gap-2 mb-2">
          <Activity size={14} className={isDark ? "text-sky-400" : "text-sky-600"} />
          <div className="flex-1">
            <div className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{t.title}</div>
            <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.subtitle}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <label className="flex flex-col gap-0.5">
            <span className={`text-[10px] uppercase tracking-wide ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.egfr_input}</span>
            <input
              type="number" inputMode="decimal" step="0.1" min="0" max="200"
              placeholder="напр. 37"
              value={egfrInput}
              onChange={(e) => setEgfrInput(e.target.value)}
              data-testid="ckd-egfr-input"
              className={`px-2 py-1 text-[12px] rounded-md border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className={`text-[10px] uppercase tracking-wide ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.weight_input}</span>
            <input
              type="number" inputMode="decimal" step="0.1" min="20" max="300"
              placeholder="напр. 70"
              value={weightInput}
              onChange={(e) => setWeightInput(e.target.value)}
              data-testid="ckd-weight-input"
              className={`px-2 py-1 text-[12px] rounded-md border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
            />
          </label>
          <button
            onClick={applyFilters}
            data-testid="ckd-apply-btn"
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-sky-500 hover:bg-sky-600 text-white transition-colors"
          >
            {t.apply}
          </button>
        </div>

        <p className={`mt-2 text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>{t.hint_manual}</p>
      </div>

      {/* Highlighted current stage + absolute limits (ако има) */}
      {currentStage && currentAbsolute && (
        <div
          className={`rounded-lg p-3 border-2 ${isDark ? "border-amber-500/40 bg-amber-500/10" : "border-amber-400 bg-amber-50"}`}
          data-testid={`ckd-your-stage-${currentStage}`}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-amber-400" : "text-amber-700"}`}>
                {t.your_stage}
              </div>
              <div className={`text-lg font-black ${isDark ? "text-white" : "text-gray-900"}`}>{currentStage}</div>
            </div>
            <div className={`text-[9px] uppercase tracking-wider font-semibold ${isDark ? "text-amber-400" : "text-amber-700"}`}>
              {t.absolute_title}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <div className={`rounded p-1.5 ${isDark ? "bg-white/5" : "bg-white"}`}>
              <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.protein}</div>
              <div className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{currentAbsolute.protein_g_day} {t.g_per_day}</div>
            </div>
            <div className={`rounded p-1.5 ${isDark ? "bg-white/5" : "bg-white"}`}>
              <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.sodium}</div>
              <div className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>&lt;{currentAbsolute.sodium_mg_day_max} {t.mg_per_day}</div>
            </div>
            <div className={`rounded p-1.5 ${isDark ? "bg-white/5" : "bg-white"}`}>
              <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.potassium}</div>
              <div className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {currentAbsolute.potassium_mg_day_min}-{currentAbsolute.potassium_mg_day_max} {t.mg_per_day}
              </div>
            </div>
            <div className={`rounded p-1.5 ${isDark ? "bg-white/5" : "bg-white"}`}>
              <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.phosphorus}</div>
              <div className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {currentAbsolute.phosphorus_mg_day_min}-{currentAbsolute.phosphorus_mg_day_max} {t.mg_per_day}
              </div>
            </div>
          </div>
          <div className={`mt-2 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            <span className="font-semibold">{t.energy}: </span>
            {currentAbsolute.energy_kcal_day_min}-{currentAbsolute.energy_kcal_day_max} {t.kcal_per_day}
          </div>
        </div>
      )}

      {/* Main table — responsive: table на desktop, cards на mobile */}
      {loading && (
        <div className={`text-center text-[12px] py-6 ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading…</div>
      )}
      {!loading && stages.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className={`w-full text-[11px] border-collapse ${isDark ? "text-gray-200" : "text-gray-800"}`}>
              <thead>
                <tr className={isDark ? "bg-white/5" : "bg-gray-100"}>
                  <th className="p-2 text-left">{t.stage}</th>
                  <th className="p-2 text-left">{t.egfr}</th>
                  <th className="p-2 text-left">{t.protein}</th>
                  <th className="p-2 text-left">{t.sodium}</th>
                  <th className="p-2 text-left">{t.potassium}</th>
                  <th className="p-2 text-left">{t.phosphorus}</th>
                  <th className="p-2 text-left">{t.energy}</th>
                </tr>
              </thead>
              <tbody>
                {stages.map((s) => {
                  const isCurrent = s.stage === currentStage;
                  return (
                    <tr
                      key={s.stage}
                      data-testid={`ckd-row-${s.stage}`}
                      className={`border-b ${isDark ? "border-white/5" : "border-gray-100"} ${isCurrent ? (isDark ? "bg-amber-500/10" : "bg-amber-50") : ""}`}
                    >
                      <td className="p-2 align-top">
                        <span
                          className="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-bold"
                          style={{ backgroundColor: s.color }}
                        >
                          {s.stage}
                        </span>
                      </td>
                      <td className="p-2 align-top font-mono text-[10px]">{s.egfr_range}</td>
                      <td className="p-2 align-top">
                        <div className="font-bold">{s.protein.g_per_kg} g/kg</div>
                        <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>{pickL(s.protein, "label", lang)}</div>
                      </td>
                      <td className="p-2 align-top">
                        <div className="font-bold">&lt;{s.sodium_mg.max} mg</div>
                        <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>{pickL(s.sodium_mg, "label", lang)}</div>
                      </td>
                      <td className="p-2 align-top">
                        <div className="font-bold">{renderRange(s.potassium_mg, "mg")}</div>
                      </td>
                      <td className="p-2 align-top">
                        <div className="font-bold">{renderRange(s.phosphorus_mg, "mg")}</div>
                      </td>
                      <td className="p-2 align-top">
                        <div className="font-bold">{s.energy_kcal_kg.min}-{s.energy_kcal_kg.max} kcal/kg</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {stages.map((s) => {
              const isCurrent = s.stage === currentStage;
              return (
                <div
                  key={`m-${s.stage}`}
                  data-testid={`ckd-card-${s.stage}`}
                  className={`rounded-lg p-2.5 border ${isCurrent ? (isDark ? "border-amber-500/40 bg-amber-500/10" : "border-amber-400 bg-amber-50") : (isDark ? "border-white/5 bg-white/5" : "border-gray-200 bg-white")}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-bold"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.stage}
                    </span>
                    <div className={`text-[11px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                      {pickL(s, "label", lang)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                    <div>
                      <span className={isDark ? "text-gray-400" : "text-gray-500"}>{t.protein}: </span>
                      <span className="font-bold">{s.protein.g_per_kg} g/kg</span>
                    </div>
                    <div>
                      <span className={isDark ? "text-gray-400" : "text-gray-500"}>{t.sodium}: </span>
                      <span className="font-bold">&lt;{s.sodium_mg.max}</span>
                    </div>
                    <div>
                      <span className={isDark ? "text-gray-400" : "text-gray-500"}>{t.potassium}: </span>
                      <span className="font-bold">{renderRange(s.potassium_mg, "")}</span>
                    </div>
                    <div>
                      <span className={isDark ? "text-gray-400" : "text-gray-500"}>{t.phosphorus}: </span>
                      <span className="font-bold">{renderRange(s.phosphorus_mg, "")}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {data?.citation && (
        <div className={`text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          <span className="font-semibold">{t.source} </span>{data.citation}
        </div>
      )}
    </div>
  );
}
